import { Suspense, useEffect, useRef, useState } from 'react';
import { css } from '@emotion/react';
import * as Sentry from '@sentry/react';
import throttle from 'lodash/throttle';
import CharacterBanner from '~/components/Main/CharacterBanner/CharacterBanner';
import CommingSoon from '~/components/Main/CommingSoon/CommingSoon';
import ExploreAllCharacters from '~/components/Main/ExploreAllCharacters/ExploreAllCharacters';
import ForYouCharacters from '~/components/Main/ForYouCharacters/ForYouCharacters';
import GameSection from '~/components/Main/GameSection/GameSection';
import GroupChatSection from '~/components/Main/GroupChatSection/GroupChatSection';
import NavigateButton from '~/components/Main/NavigateButton/NavigateButton';
import NeedHelp from '~/components/Main/NeedHelp/NeedHelp';
import { FlexStack } from '~/components/atomic';
import ChatPageLoadingLayout from '~/components/common/ChatPageLoadingLayout/ChatPageLoadingLayout';
import { MainSkeleton } from '~/components/common/Skeleton';
import { airbridgeEvent } from '~/utils/airbridge';
import { useUserStore } from '~/store';
import useChatPageLoadingStatus from '~/store/useChatPageLoadingStatusStore';
import * as S from './index.styled';

export default function Explore() {
  const [viewState, setViewState] = useState<boolean>(false);
  const [windowWidth, setWindowWidth] = useState<number>(window.innerWidth);

  const groupRef = useRef<HTMLDivElement>(null);
  const email = useUserStore((state) => state.email);

  const { isChatPageLoading, setChatPageLoadingEnd } =
    useChatPageLoadingStatus();

  useEffect(() => {
    airbridgeEvent.homeView(email);
  }, []);

  useEffect(() => {
    const handleScroll = throttle(() => {
      const scrollPosition = window.scrollY;
      const documentHeight = document.documentElement.scrollHeight;

      if (scrollPosition > documentHeight * 0.2) {
        setViewState(true);
      } else {
        setViewState(false);
      }
    }, 100);

    const handleResize = throttle(() => {
      const currentWidth = window.innerWidth > 768 ? 768 : window.innerWidth;
      setWindowWidth(currentWidth > 768 ? 768 : currentWidth);
    }, 100);

    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
    };
  }, [windowWidth]);

  const handleNavigateClick = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  useEffect(() => {
    if (isChatPageLoading) setChatPageLoadingEnd();
  }, []);

  return (
    <>
      <ChatPageLoadingLayout />
      <Suspense fallback={<MainSkeleton />}>
        <S.Container>
          {viewState && (
            <NavigateButton
              viewState={viewState}
              windowWidth={windowWidth}
              handleNavigateClick={handleNavigateClick}
            />
          )}
          <S.ImageContainer>
            <Sentry.ErrorBoundary fallback={undefined}>
              <CharacterBanner />
            </Sentry.ErrorBoundary>
          </S.ImageContainer>

          <FlexStack
            spacing={16}
            css={css`
              padding-top: 40px;
            `}
          >
            <FlexStack
              spacing={24}
              css={css`
                padding-left: 16px;
              `}
            >
              <FlexStack
                spacing={32}
                css={css`
                  padding-bottom: 48px;
                `}
              >
                <Sentry.ErrorBoundary fallback={undefined}>
                  <GameSection />
                </Sentry.ErrorBoundary>

                <Sentry.ErrorBoundary fallback={undefined}>
                  <NeedHelp />
                </Sentry.ErrorBoundary>

                <CommingSoon />

                <Sentry.ErrorBoundary fallback={undefined}>
                  <ForYouCharacters />
                </Sentry.ErrorBoundary>

                <Sentry.ErrorBoundary fallback={undefined}>
                  <ExploreAllCharacters />
                </Sentry.ErrorBoundary>
              </FlexStack>
            </FlexStack>

            <div
              css={css`
                padding: 0 16px;
              `}
              ref={groupRef}
            >
              <FlexStack spacing={16}>
                <S.SubTitle>Group chat</S.SubTitle>
                <Sentry.ErrorBoundary fallback={undefined}>
                  <GroupChatSection />
                </Sentry.ErrorBoundary>
              </FlexStack>
            </div>
          </FlexStack>
        </S.Container>
      </Suspense>
    </>
  );
}
